import React, { useMemo } from 'react';
import type { PriceAlert, AlertDelivery } from '@catalyst-iq/shared';

interface AlertWithDeliveries extends PriceAlert {
  alert_deliveries?: AlertDelivery[];
}

interface AlertsStatsProps {
  alerts: AlertWithDeliveries[];
}

export const AlertsStats: React.FC<AlertsStatsProps> = ({ alerts }) => {
  const stats = useMemo(() => {
    const todayStr = new Date().toDateString();

    let active = 0;
    let triggeredToday = 0;
    let deliveredTelegram = 0;
    let deliveredInApp = 0;
    let failed = 0;

    (alerts || []).forEach((alert) => {
      if (alert.status === 'active') {
        active++;
      }

      if (alert.status === 'triggered' && alert.triggered_at) {
        const triggeredDate = new Date(alert.triggered_at).toDateString();
        if (triggeredDate === todayStr) {
          triggeredToday++;
        }
      }

      const deliveries = Array.isArray(alert.alert_deliveries) ? alert.alert_deliveries : [];

      let hasTelegramSuccess = false;
      let hasInAppSuccess = false;
      let hasFailure = false;

      deliveries.forEach((delivery) => {
        const channel = delivery.channel?.toLowerCase() || '';
        const status = delivery.status?.toLowerCase() || '';

        if (status === 'sent' || status === 'delivered') {
          if (channel === 'telegram') {
            hasTelegramSuccess = true;
          }
          if (channel === 'in-app' || channel === 'in_app' || channel === 'app') {
            hasInAppSuccess = true;
          }
        }

        if (status === 'failed') {
          hasFailure = true;
        }
      });

      if (hasTelegramSuccess) deliveredTelegram++;
      if (hasInAppSuccess) deliveredInApp++;
      if (hasFailure) failed++;
    });

    return {
      active,
      triggeredToday,
      deliveredTelegram,
      deliveredInApp,
      failed,
    };
  }, [alerts]);

  return (
    <div className="relative flex items-center gap-2.5 flex-wrap">
      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/80 dark:bg-[#0e1624]/80 border border-gray-100 dark:border-[#1d2d47] shadow-sm">
        <div>
          <div className="text-[18px] font-bold font-mono tabular-nums text-gray-900 dark:text-[#f1f5fb] leading-none">
            {stats.active}
          </div>
          <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">
            Active
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#10d98e]/8 dark:bg-[#10d98e]/10 border border-[#10d98e]/20">
        <span className="w-2 h-2 rounded-full bg-[#10d98e] shrink-0" />
        <div>
          <div className="text-[18px] font-bold font-mono tabular-nums text-[#10d98e] leading-none">
            {stats.triggeredToday}
          </div>
          <div className="text-[10px] text-[#10d98e]/70 mt-0.5 uppercase tracking-wide font-semibold">
            Triggered Today
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/80 dark:bg-[#0e1624]/80 border border-gray-100 dark:border-[#1d2d47] shadow-sm">
        <div>
          <div className="text-[18px] font-bold font-mono tabular-nums text-gray-900 dark:text-[#f1f5fb] leading-none">
            {stats.deliveredTelegram}
          </div>
          <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">
            Delivered • Telegram
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/80 dark:bg-[#0e1624]/80 border border-gray-100 dark:border-[#1d2d47] shadow-sm">
        <div>
          <div className="text-[18px] font-bold font-mono tabular-nums text-gray-900 dark:text-[#f1f5fb] leading-none">
            {stats.deliveredInApp}
          </div>
          <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">
            Delivered • In-App
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-gray-100/80 dark:bg-[#1d2d47]/30 border border-gray-200 dark:border-[#1d2d47]">
        <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-[#a3b6cc] shrink-0" />
        <div>
          <div className="text-[18px] font-bold font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0] leading-none">
            {stats.failed}
          </div>
          <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">
            Failed
          </div>
        </div>
      </div>
    </div>
  );
};
